import { scaleStatus } from '../lib/scale.js'
import ToothIcon from './icons/ToothIcon.jsx'

// "ขูดหินปูน" recall tag — shown for a patient whose last scaling was never done
// or was more than ~6 months ago (an upsell/recall hook). Renders nothing when
// the patient is up to date. `compact` = icon only (for tight rows).
//
// Two visually distinct states, so the row is readable without hovering:
//   never   → solid tooth,   rose   ("ไม่เคยขูดหินปูน" — no scaling billing on record)
//   overdue → outline tooth, amber  ("ครบกำหนด" — last scaling > 6 months ago)
//
// Pass either a source object carrying scale_date (patient record or dashboard
// appt row) via `src`, or a precomputed status via `status`.

const MONTH_MS = 30.4 * 24 * 60 * 60 * 1000

// Visual + copy for each state, keyed by st.never.
function look(st) {
  if (st.never) {
    return {
      filled: true,
      label: 'ไม่เคยขูดหินปูน',
      title: 'ไม่เคยขูดหินปูน — ไม่มีประวัติขูดหินปูนในระบบ · เสนอขูดหินปูนได้',
      icon: 'text-rose-500',
      pill: 'bg-rose-50 text-rose-700 ring-rose-200',
    }
  }
  const months = st.ms ? Math.floor((Date.now() - st.ms) / MONTH_MS) : null
  return {
    filled: false,
    label: 'ขูดหินปูนครบกำหนด',
    title: months != null
      ? `ขูดหินปูนครั้งล่าสุด ${months} เดือนที่แล้ว — ครบกำหนด (เกิน 6 เดือน)`
      : 'ขูดหินปูนครั้งล่าสุดเกิน 6 เดือน — ครบกำหนด',
    icon: 'text-amber-500',
    pill: 'bg-amber-50 text-amber-700 ring-amber-200',
  }
}

export default function ScaleTag({ src, status, compact = false, size = 15, className = '' }) {
  const st = status || scaleStatus(src)
  if (!st.overdue) return null
  const { filled, label, title, icon, pill } = look(st)

  if (compact) {
    return (
      <span title={title} className={`inline-flex items-center justify-center ${icon} ${className}`}>
        <ToothIcon size={size} filled={filled} strokeWidth={2} />
      </span>
    )
  }

  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium ring-1 whitespace-nowrap ${pill} ${className}`}
    >
      <ToothIcon size={13} filled={filled} strokeWidth={2} /> {label}
    </span>
  )
}

// Key for the compact icons, for lists that show them (huddle queue, calendar).
export function ScaleLegend({ className = '' }) {
  return (
    <span className={`inline-flex items-center gap-3 text-[11px] text-slate-400 ${className}`}>
      <span className="inline-flex items-center gap-1">
        <ToothIcon size={13} filled className="text-rose-500" /> ไม่เคยขูดหินปูน
      </span>
      <span className="inline-flex items-center gap-1">
        <ToothIcon size={13} className="text-amber-500" /> ครบกำหนด 6 เดือน
      </span>
    </span>
  )
}
